/**
 * Locale configuration for all supported countries and languages
 * Ported from turn-v1/src/lib/i18n/locales.ts
 * 
 * Used by the trial request form for country/language selection
 */

export type Locale = string;
export type Country = string;
export type Language = string;

export interface LocaleConfig {
  locale: Locale;
  country: Country;
  language: Language;
  flag: string;
  displayName: string;
  /** E.164 country calling code (e.g., '1' for US/Canada, '44' for UK) */
  callingCode: string;
}

// All supported locales with E.164 calling codes
export const locales: LocaleConfig[] = [
  // Australia (+61)
  { locale: 'au-en', country: 'Australia', language: 'English', flag: '🇦🇺', displayName: 'Australia / English', callingCode: '61' },
  // Austria (+43)
  { locale: 'at-de', country: 'Austria', language: 'Deutsch', flag: '🇦🇹', displayName: 'Austria / Deutsch', callingCode: '43' },
  { locale: 'at-en', country: 'Austria', language: 'English', flag: '🇦🇹', displayName: 'Austria / English', callingCode: '43' },
  // Belgium (+32)
  { locale: 'be-nl', country: 'Belgium', language: 'Nederlands', flag: '🇧🇪', displayName: 'Belgium / Nederlands', callingCode: '32' },
  { locale: 'be-fr', country: 'Belgium', language: 'Français', flag: '🇧🇪', displayName: 'Belgium / Français', callingCode: '32' },
  { locale: 'be-de', country: 'Belgium', language: 'Deutsch', flag: '🇧🇪', displayName: 'Belgium / Deutsch', callingCode: '32' },
  { locale: 'be-en', country: 'Belgium', language: 'English', flag: '🇧🇪', displayName: 'Belgium / English', callingCode: '32' },
  // Brazil (+55)
  { locale: 'br-pt', country: 'Brazil', language: 'Português', flag: '🇧🇷', displayName: 'Brazil / Português', callingCode: '55' },
  { locale: 'br-en', country: 'Brazil', language: 'English', flag: '🇧🇷', displayName: 'Brazil / English', callingCode: '55' },
  // Bulgaria (+359)
  { locale: 'bg-en', country: 'Bulgaria', language: 'English', flag: '🇧🇬', displayName: 'Bulgaria / English', callingCode: '359' },
  // Canada (+1)
  { locale: 'ca-en', country: 'Canada', language: 'English', flag: '🇨🇦', displayName: 'Canada / English', callingCode: '1' },
  { locale: 'ca-fr', country: 'Canada', language: 'Français', flag: '🇨🇦', displayName: 'Canada / Français', callingCode: '1' },
  // Croatia (+385)
  { locale: 'hr-en', country: 'Croatia', language: 'English', flag: '🇭🇷', displayName: 'Croatia / English', callingCode: '385' },
  { locale: 'hr-it', country: 'Croatia', language: 'Italiano', flag: '🇭🇷', displayName: 'Croatia / Italiano', callingCode: '385' },
  // Cyprus (+357)
  { locale: 'cy-en', country: 'Cyprus', language: 'English', flag: '🇨🇾', displayName: 'Cyprus / English', callingCode: '357' },
  // Czech Republic (+420)
  { locale: 'cz-en', country: 'Czech Republic', language: 'English', flag: '🇨🇿', displayName: 'Czech Republic / English', callingCode: '420' },
  // Denmark (+45)
  { locale: 'dk-en', country: 'Denmark', language: 'English', flag: '🇩🇰', displayName: 'Denmark / English', callingCode: '45' },
  // Estonia (+372)
  { locale: 'ee-en', country: 'Estonia', language: 'English', flag: '🇪🇪', displayName: 'Estonia / English', callingCode: '372' },
  // Finland (+358)
  { locale: 'fi-en', country: 'Finland', language: 'English', flag: '🇫🇮', displayName: 'Finland / English', callingCode: '358' },
  { locale: 'fi-sv', country: 'Finland', language: 'Svenska', flag: '🇫🇮', displayName: 'Finland / Svenska', callingCode: '358' },
  // France (+33)
  { locale: 'fr-fr', country: 'France', language: 'Français', flag: '🇫🇷', displayName: 'France / Français', callingCode: '33' },
  { locale: 'fr-en', country: 'France', language: 'English', flag: '🇫🇷', displayName: 'France / English', callingCode: '33' },
  // Germany (+49)
  { locale: 'de-de', country: 'Germany', language: 'Deutsch', flag: '🇩🇪', displayName: 'Germany / Deutsch', callingCode: '49' },
  { locale: 'de-en', country: 'Germany', language: 'English', flag: '🇩🇪', displayName: 'Germany / English', callingCode: '49' },
  // Gibraltar (+350)
  { locale: 'gi-en', country: 'Gibraltar', language: 'English', flag: '🇬🇮', displayName: 'Gibraltar / English', callingCode: '350' },
  // Greece (+30)
  { locale: 'gr-en', country: 'Greece', language: 'English', flag: '🇬🇷', displayName: 'Greece / English', callingCode: '30' },
  // Hong Kong (+852)
  { locale: 'hk-en', country: 'Hong Kong', language: 'English', flag: '🇭🇰', displayName: 'Hong Kong / English', callingCode: '852' },
  { locale: 'hk-zh', country: 'Hong Kong', language: '简体中文', flag: '🇭🇰', displayName: 'Hong Kong / 简体中文', callingCode: '852' },
  // Hungary (+36)
  { locale: 'hu-en', country: 'Hungary', language: 'English', flag: '🇭🇺', displayName: 'Hungary / English', callingCode: '36' },
  // India (+91)
  { locale: 'in-en', country: 'India', language: 'English', flag: '🇮🇳', displayName: 'India / English', callingCode: '91' },
  // Ireland (+353)
  { locale: 'ie-en', country: 'Ireland', language: 'English', flag: '🇮🇪', displayName: 'Ireland / English', callingCode: '353' },
  // Italy (+39)
  { locale: 'it-it', country: 'Italy', language: 'Italiano', flag: '🇮🇹', displayName: 'Italy / Italiano', callingCode: '39' },
  { locale: 'it-en', country: 'Italy', language: 'English', flag: '🇮🇹', displayName: 'Italy / English', callingCode: '39' },
  // Japan (+81)
  { locale: 'jp-ja', country: 'Japan', language: '日本語', flag: '🇯🇵', displayName: 'Japan / 日本語', callingCode: '81' },
  { locale: 'jp-en', country: 'Japan', language: 'English', flag: '🇯🇵', displayName: 'Japan / English', callingCode: '81' },
  // Latvia (+371)
  { locale: 'lv-en', country: 'Latvia', language: 'English', flag: '🇱🇻', displayName: 'Latvia / English', callingCode: '371' },
  // Liechtenstein (+423)
  { locale: 'li-de', country: 'Liechtenstein', language: 'Deutsch', flag: '🇱🇮', displayName: 'Liechtenstein / Deutsch', callingCode: '423' },
  { locale: 'li-en', country: 'Liechtenstein', language: 'English', flag: '🇱🇮', displayName: 'Liechtenstein / English', callingCode: '423' },
  // Lithuania (+370)
  { locale: 'lt-en', country: 'Lithuania', language: 'English', flag: '🇱🇹', displayName: 'Lithuania / English', callingCode: '370' },
  // Luxembourg (+352)
  { locale: 'lu-fr', country: 'Luxembourg', language: 'Français', flag: '🇱🇺', displayName: 'Luxembourg / Français', callingCode: '352' },
  { locale: 'lu-de', country: 'Luxembourg', language: 'Deutsch', flag: '🇱🇺', displayName: 'Luxembourg / Deutsch', callingCode: '352' },
  { locale: 'lu-en', country: 'Luxembourg', language: 'English', flag: '🇱🇺', displayName: 'Luxembourg / English', callingCode: '352' },
  // Malaysia (+60)
  { locale: 'my-en', country: 'Malaysia', language: 'English', flag: '🇲🇾', displayName: 'Malaysia / English', callingCode: '60' },
  { locale: 'my-zh', country: 'Malaysia', language: '简体中文', flag: '🇲🇾', displayName: 'Malaysia / 简体中文', callingCode: '60' },
  // Malta (+356)
  { locale: 'mt-en', country: 'Malta', language: 'English', flag: '🇲🇹', displayName: 'Malta / English', callingCode: '356' },
  // Mexico (+52)
  { locale: 'mx-es', country: 'Mexico', language: 'Español', flag: '🇲🇽', displayName: 'Mexico / Español', callingCode: '52' },
  { locale: 'mx-en', country: 'Mexico', language: 'English', flag: '🇲🇽', displayName: 'Mexico / English', callingCode: '52' },
  // Netherlands (+31)
  { locale: 'nl-nl', country: 'Netherlands', language: 'Nederlands', flag: '🇳🇱', displayName: 'Netherlands / Nederlands', callingCode: '31' },
  { locale: 'nl-en', country: 'Netherlands', language: 'English', flag: '🇳🇱', displayName: 'Netherlands / English', callingCode: '31' },
  // New Zealand (+64)
  { locale: 'nz-en', country: 'New Zealand', language: 'English', flag: '🇳🇿', displayName: 'New Zealand / English', callingCode: '64' },
  // Norway (+47)
  { locale: 'no-en', country: 'Norway', language: 'English', flag: '🇳🇴', displayName: 'Norway / English', callingCode: '47' },
  // Poland (+48)
  { locale: 'pl-en', country: 'Poland', language: 'English', flag: '🇵🇱', displayName: 'Poland / English', callingCode: '48' },
  // Portugal (+351)
  { locale: 'pt-pt', country: 'Portugal', language: 'Português', flag: '🇵🇹', displayName: 'Portugal / Português', callingCode: '351' },
  { locale: 'pt-en', country: 'Portugal', language: 'English', flag: '🇵🇹', displayName: 'Portugal / English', callingCode: '351' },
  // Romania (+40)
  { locale: 'ro-en', country: 'Romania', language: 'English', flag: '🇷🇴', displayName: 'Romania / English', callingCode: '40' },
  // Singapore (+65)
  { locale: 'sg-en', country: 'Singapore', language: 'English', flag: '🇸🇬', displayName: 'Singapore / English', callingCode: '65' },
  { locale: 'sg-zh', country: 'Singapore', language: '简体中文', flag: '🇸🇬', displayName: 'Singapore / 简体中文', callingCode: '65' },
  // Slovakia (+421)
  { locale: 'sk-en', country: 'Slovakia', language: 'English', flag: '🇸🇰', displayName: 'Slovakia / English', callingCode: '421' },
  // Slovenia (+386)
  { locale: 'si-en', country: 'Slovenia', language: 'English', flag: '🇸🇮', displayName: 'Slovenia / English', callingCode: '386' },
  { locale: 'si-it', country: 'Slovenia', language: 'Italiano', flag: '🇸🇮', displayName: 'Slovenia / Italiano', callingCode: '386' },
  // Spain (+34)
  { locale: 'es-es', country: 'Spain', language: 'Español', flag: '🇪🇸', displayName: 'Spain / Español', callingCode: '34' },
  { locale: 'es-en', country: 'Spain', language: 'English', flag: '🇪🇸', displayName: 'Spain / English', callingCode: '34' },
  // Sweden (+46)
  { locale: 'se-sv', country: 'Sweden', language: 'Svenska', flag: '🇸🇪', displayName: 'Sweden / Svenska', callingCode: '46' },
  { locale: 'se-en', country: 'Sweden', language: 'English', flag: '🇸🇪', displayName: 'Sweden / English', callingCode: '46' },
  // Switzerland (+41)
  { locale: 'ch-de', country: 'Switzerland', language: 'Deutsch', flag: '🇨🇭', displayName: 'Switzerland / Deutsch', callingCode: '41' },
  { locale: 'ch-fr', country: 'Switzerland', language: 'Français', flag: '🇨🇭', displayName: 'Switzerland / Français', callingCode: '41' },
  { locale: 'ch-it', country: 'Switzerland', language: 'Italiano', flag: '🇨🇭', displayName: 'Switzerland / Italiano', callingCode: '41' },
  { locale: 'ch-en', country: 'Switzerland', language: 'English', flag: '🇨🇭', displayName: 'Switzerland / English', callingCode: '41' },
  // Thailand (+66)
  { locale: 'th-th', country: 'Thailand', language: 'ไทย', flag: '🇹🇭', displayName: 'Thailand / ไทย', callingCode: '66' },
  { locale: 'th-en', country: 'Thailand', language: 'English', flag: '🇹🇭', displayName: 'Thailand / English', callingCode: '66' },
  // United Arab Emirates (+971)
  { locale: 'ae-en', country: 'United Arab Emirates', language: 'English', flag: '🇦🇪', displayName: 'United Arab Emirates / English', callingCode: '971' },
  // United Kingdom (+44)
  { locale: 'gb-en', country: 'United Kingdom', language: 'English', flag: '🇬🇧', displayName: 'United Kingdom / English', callingCode: '44' },
  // United States (+1)
  { locale: 'us-en', country: 'United States', language: 'English', flag: '🇺🇸', displayName: 'United States / English', callingCode: '1' },
  { locale: 'us-es', country: 'United States', language: 'Español', flag: '🇺🇸', displayName: 'United States / Español', callingCode: '1' },
  { locale: 'us-zh', country: 'United States', language: '简体中文', flag: '🇺🇸', displayName: 'United States / 简体中文', callingCode: '1' },
];

export const defaultLocale: Locale = 'us-en';

/**
 * Get locale configuration by locale code
 */
export function getLocaleConfig(locale: Locale): LocaleConfig | undefined {
  return locales.find(l => l.locale === locale);
}

/**
 * Check if a locale code is valid
 */
export function isValidLocale(locale: string): boolean {
  return locales.some(l => l.locale === locale);
}

/**
 * Extract language code from locale (e.g., 'us-en' -> 'en')
 * Returns a supported language code for i18n
 */
export function getLanguageCode(locale: Locale): 'en' | 'es' | 'de' | 'fr' | 'it' | 'ja' | 'nl' | 'pt' | 'sv' | 'th' | 'zh' {
  const code = locale.split('-')[1] || 'en';
  // Map to supported language codes
  const supported = ['en', 'es', 'de', 'fr', 'it', 'ja', 'nl', 'pt', 'sv', 'th', 'zh'] as const;
  return supported.includes(code as any) ? (code as typeof supported[number]) : 'en';
}

/**
 * Get all unique countries
 */
export function getCountries(): string[] {
  return [...new Set(locales.map(l => l.country))].sort();
}

/**
 * Get all locales for a specific country
 */
export function getLocalesForCountry(country: string): LocaleConfig[] {
  return locales.filter(l => l.country === country);
}
